import React, { useEffect, useMemo, useState } from "react";
import { useTipsDisplay } from "@/hooks/useTipsDisplay";
import { ArrowUpDown, Clock, Heart, MapPin } from "lucide-react";
import RaceCountdown from "@/components/RaceCountdown";
import BottomNavbar from "@/components/bottom-navbar";
import { useCarousel } from "@/components/layouts/RacingLayout";
import MarketStartTime from "@/components/market-start-time";
import logger from "@/utils/logger";
import { minTips } from "@/utils/vars";
import { getStoredProductName } from "@/utils/product";

export default function MyTips() {
  // Get the carousel ref from context instead of creating a new one
  const { ref: carouselRef } = useCarousel();
  const [favoriteTips, setFavoriteTips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortByTime, setSortByTime] = useState(false);
  // Use the tips display hook to control how many tips to show
  const { showAllTips, tipsCount } = useTipsDisplay();
  const [isLexiSocial, setIsLexiSocial] = useState(false);

  // Detect product to determine Lexi Social (UT-FREE)
  useEffect(() => {
    const resolveProduct = () => {
      const product = (getStoredProductName() || "").toLowerCase();
      setIsLexiSocial(product === "lexi social");
    };

    resolveProduct();
    const handleProductChange = () => resolveProduct();
    window.addEventListener("lexi:product-updated", handleProductChange);
    return () => {
      window.removeEventListener("lexi:product-updated", handleProductChange);
    };
  }, []);

  // Force UT-FREE to only see the top 2 tips
  const tipsLimit = isLexiSocial ? minTips : tipsCount;

  // Load favorite tips from localStorage on component mount
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Load favorites from localStorage
    const loadFavorites = () => {
      try {
        setIsLoading(true);
        const storedTips = JSON.parse(
          localStorage.getItem("favoriteTips") || "[]",
        );

        // Filter out expired tips (after 10am the next day)
        const now = new Date();
        const validTips = storedTips.filter((tip) => {
          return new Date(tip.expiresAt) > now;
        });

        // Update localStorage if we removed expired tips
        if (validTips.length !== storedTips.length) {
          localStorage.setItem("favoriteTips", JSON.stringify(validTips));
          // Dispatch event to notify other components about the change
          window.dispatchEvent(new Event("favoritesUpdated"));
        }

        setFavoriteTips(validTips);
      } catch (error) {
        logger.error("Error loading favorite tips:", error);
        setFavoriteTips([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
    logger.log(favoriteTips);
    // Also set up a listener for storage changes (in case tips are added/removed in another tab)
    window.addEventListener("storage", loadFavorites);

    // Listen for the custom event for changes within the same tab
    window.addEventListener("favoritesUpdated", loadFavorites);

    return () => {
      window.removeEventListener("storage", loadFavorites);
      window.removeEventListener("favoritesUpdated", loadFavorites);
    };
  }, []);

  // Toggle sort method
  const toggleSortMethod = () => {
    setSortByTime(!sortByTime);
  };

  // Group and sort favorite tips by track and race number or by time
  const organizedFavorites = useMemo(() => {
    if (sortByTime) {
      // Sort by market start time when sortByTime is true
      const sortedByTime = [...favoriteTips].sort((a, b) => {
        const timeA = a.marketStartTime
          ? new Date(a.marketStartTime)
          : new Date(0);
        const timeB = b.marketStartTime
          ? new Date(b.marketStartTime)
          : new Date(0);
        return timeA - timeB;
      });

      // Return a flat list format for time-based sorting
      return {
        byTime: sortedByTime,
        format: "time",
      };
    } else {
      // Group favorites by track when sorting by track (default)
      const grouped = {};

      // Group favorites by track
      favoriteTips.forEach((favorite) => {
        const track = favorite.track || "Unknown";
        if (!grouped[track]) {
          grouped[track] = [];
        }
        grouped[track].push(favorite);
      });

      // Sort tracks alphabetically and races by number within each track
      const byTrack = Object.keys(grouped)
        .sort()
        .map((track) => {
          return {
            track,
            races: grouped[track].sort((a, b) => {
              // Convert race numbers to integers for proper numerical sorting
              const raceA = parseInt(a.race, 10) || 0;
              const raceB = parseInt(b.race, 10) || 0;
              return raceA - raceB;
            }),
          };
        });

      return {
        byTrack,
        format: "track",
      };
    }
  }, [favoriteTips, sortByTime]);

  // Remove a tip from favorites
  const removeFavorite = (raceKey) => {
    try {
      const updatedFavorites = favoriteTips.filter(
        (tip) => tip.raceKey !== raceKey,
      );
      localStorage.setItem("favoriteTips", JSON.stringify(updatedFavorites));
      setFavoriteTips(updatedFavorites);

      // Dispatch event to notify other components about the change
      window.dispatchEvent(new Event("favoritesUpdated"));
    } catch (error) {
      logger.error("Error removing favorite:", error);
    }
  };

  // Navigate to the tip detail page
  const navigateToTip = (favorite) => {
    if (!favorite.track || !favorite.race) return;

    const formattedTrackName = favorite.track
      .replace(/\s+/g, "-")
      .toLowerCase();
    const todaysTipsPath = `/todays-tips?track=${formattedTrackName}&race=${favorite.race}`;

    // Use client-side routing if available
    if (window.navigate) {
      window.navigate(todaysTipsPath);
    } else if (
      window.clientSideRouter &&
      typeof window.clientSideRouter.navigate === "function"
    ) {
      window.clientSideRouter.navigate(todaysTipsPath);
    } else {
      // Fallback to history API
      window.history.pushState({}, "", todaysTipsPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-md mx-auto">
        {/* Content */}
        <div className="px-4 py-24">
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-pink-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your tips...</p>
            </div>
          ) : favoriteTips.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">My Tips</h1>
              <p className="text-gray-600">
                Find your favourite tips and add them to see them here. Results
                updated throughout the day.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-900">My Tips</h1>
                <div
                  className="bg-gray-100 p-1 rounded-full flex items-center"
                  data-gtm-element-id="my-tips_sort-toggle_button-1"
                >
                  <button
                    onClick={() => setSortByTime(false)}
                    className={`flex items-center gap-1.5 text-sm py-1.5 px-3 rounded-full transition-colors ${
                      !sortByTime
                        ? "bg-white text-[#F30099] shadow-sm font-medium"
                        : "text-gray-600 hover:bg-gray-200"
                    }`}
                    aria-pressed={!sortByTime}
                    data-gtm-element-id="my-tips_sort-by-track_button-1"
                  >
                    <div className="relative">
                      <MapPin size={14} />
                      {!sortByTime && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#F30099] rounded-full"></span>
                      )}
                    </div>
                    <span>By Track</span>
                  </button>
                  <button
                    onClick={() => setSortByTime(true)}
                    className={`flex items-center gap-1.5 text-sm py-1.5 px-3 rounded-full transition-colors ${
                      sortByTime
                        ? "bg-white text-[#F30099] shadow-sm font-medium"
                        : "text-gray-600 hover:bg-gray-200"
                    }`}
                    aria-pressed={sortByTime}
                    data-gtm-element-id="my-tips_sort-by-time_button-1"
                  >
                    <div className="relative">
                      <Clock size={14} />
                      {sortByTime && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#F30099] rounded-full"></span>
                      )}
                    </div>
                    <span>By Time</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center text-xs text-gray-500 mb-4">
                <span>Sorted by: </span>
                <span className="font-medium text-[#F30099] ml-1 flex items-center">
                  {sortByTime ? (
                    <>
                      <Clock size={12} className="mr-1" /> Race times
                    </>
                  ) : (
                    <>
                      <MapPin size={12} className="mr-1" /> Track names
                    </>
                  )}
                </span>
              </div>

              {organizedFavorites.format === "track" ? (
                // Display grouped by track
                organizedFavorites.byTrack.map((trackGroup) => (
                  <div key={trackGroup.track} className="track-group">
                    <h2 className="text-xl font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                      {trackGroup.track}
                    </h2>

                    <div className="space-y-4">
                      {trackGroup.races.map((favorite) => (
                        <div
                          key={favorite.raceKey}
                          className="bg-white rounded-lg shadow-sm overflow-hidden"
                        >
                          <div
                            className="flex justify-between items-center p-4 border-b border-gray-100 cursor-pointer"
                            onClick={() => navigateToTip(favorite)}
                            data-gtm-element-id="my-tips_navigate-to-tip_button-1"
                          >
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                Race {favorite.race}
                                {favorite.marketStartTime && (
                                  <span className="text-brand-pink ml-2 text-sm bg-pink-50 py-0.5 px-2 rounded-full inline-flex items-center">
                                    {new Date(favorite.marketStartTime) >
                                    new Date() ? (
                                      <RaceCountdown
                                        raceId={favorite.raceKey}
                                        marketStartTime={
                                          favorite.marketStartTime
                                        }
                                        variant="compact"
                                        showSeconds={
                                          (new Date(favorite.marketStartTime) -
                                            new Date()) /
                                            1000 <
                                          60
                                        }
                                      />
                                    ) : (
                                      <MarketStartTime
                                        marketStartTime={
                                          favorite.marketStartTime
                                        }
                                        variant="tips"
                                      />
                                    )}
                                  </span>
                                )}
                              </h3>
                            </div>

                            <button
                              data-gtm-element-id="my-tips_remove-favorite_button-1"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent navigating to the tip when removing
                                removeFavorite(favorite.raceKey);
                              }}
                              className="text-gray-400 hover:text-red-500 focus:outline-none"
                              aria-label="Remove from favorites"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="deeppink"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                />
                              </svg>
                            </button>
                          </div>

                          <div
                            className="p-4 cursor-pointer"
                            onClick={() => navigateToTip(favorite)}
                            data-gtm-element-id="my-tips_navigate-to-tip_button-2"
                          >
                            {favorite.tips && favorite.tips.length > 0 ? (
                              favorite.tips.map((tip, index) => {
                                // Only show the number of tips based on user preference
                                if (index >= tipsLimit) {
                                  return null;
                                }

                                return (
                                  <div
                                    key={index}
                                    className="flex items-center gap-3 mb-2 last:mb-0"
                                  >
                                    <div className="w-8 h-8">
                                      {tip.silks && (
                                        <img
                                          src={tip.silks}
                                          alt="Jockey silk"
                                          className="w-8 h-8 object-contain"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      )}
                                    </div>
                                    <div className="font-semibold">
                                      {tip.number}.
                                    </div>
                                    <div className="font-semibold">
                                      {tip.name}
                                    </div>

                                    {/* Show result indicators if available */}
                                    {tip.result && (
                                      <div className="ml-auto">
                                        {tip.result === "winner" && (
                                          <span className="text-sm font-medium bg-pink-600 text-white py-1 px-2 rounded">
                                            Winner
                                          </span>
                                        )}
                                        {tip.result === "placed" && (
                                          <span className="text-sm font-medium bg-blue-600 text-white py-1 px-2 rounded">
                                            Placed
                                          </span>
                                        )}
                                        {tip.result === "unplaced" && (
                                          <span className="text-sm font-medium bg-gray-200 text-gray-600 py-1 px-2 rounded">
                                            Unplaced
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-gray-500 text-sm">
                                No tips available for this race
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                // Display sorted by time
                <div className="space-y-4">
                  {organizedFavorites.byTime.map((favorite) => (
                    <div
                      key={favorite.raceKey}
                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                    >
                      <div
                        className="flex justify-between items-center p-4 border-b border-gray-100 cursor-pointer"
                        onClick={() => navigateToTip(favorite)}
                        data-gtm-element-id="my-tips_navigate-to-tip_button-3"
                      >
                        <div>
                          <div className="font-semibold text-gray-700 text-sm mb-1">
                            {favorite.track}
                          </div>
                          <h3 className="font-semibold text-gray-900">
                            Race {favorite.race}
                            {favorite.marketStartTime && (
                              <span className="text-brand-pink ml-2 text-sm bg-pink-50 py-0.5 px-2 rounded-full inline-flex items-center">
                                {new Date(favorite.marketStartTime) >
                                new Date() ? (
                                  <RaceCountdown
                                    raceId={favorite.raceKey}
                                    marketStartTime={favorite.marketStartTime}
                                    variant="compact"
                                    showSeconds={
                                      (new Date(favorite.marketStartTime) -
                                        new Date()) /
                                        1000 <
                                      60
                                    }
                                  />
                                ) : (
                                  <MarketStartTime
                                    marketStartTime={favorite.marketStartTime}
                                    variant="tips"
                                  />
                                )}
                              </span>
                            )}
                          </h3>
                        </div>

                        <button
                          data-gtm-element-id="my-tips_remove-favorite_button-2"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent navigating to the tip when removing
                            removeFavorite(favorite.raceKey);
                          }}
                          className="text-gray-400 hover:text-red-500 focus:outline-none"
                          aria-label="Remove from favorites"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="deeppink"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                            />
                          </svg>
                        </button>
                      </div>

                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => navigateToTip(favorite)}
                        data-gtm-element-id="my-tips_navigate-to-tip_button-4"
                      >
                        {favorite.tips && favorite.tips.length > 0 ? (
                          favorite.tips.map((tip, index) => {
                            // Only show the number of tips based on user preference (UT-FREE capped)
                            if (index >= tipsLimit) {
                              return null;
                            }

                            return (
                              <div
                                key={index}
                                className="flex items-center gap-3 mb-2 last:mb-0"
                              >
                                <div className="w-8 h-8">
                                  {tip.silks && (
                                    <img
                                      src={tip.silks}
                                      alt="Jockey silk"
                                      className="w-8 h-8 object-contain"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="font-semibold">
                                  {tip.number}.
                                </div>
                                <div className="font-semibold">{tip.name}</div>

                                {/* Show result indicators if available */}
                                {tip.result && (
                                  <div className="ml-auto">
                                    {tip.result === "winner" && (
                                      <span className="text-sm font-medium bg-pink-600 text-white py-1 px-2 rounded">
                                        Winner
                                      </span>
                                    )}
                                    {tip.result === "placed" && (
                                      <span className="text-sm font-medium bg-blue-600 text-white py-1 px-2 rounded">
                                        Placed
                                      </span>
                                    )}
                                    {tip.result === "unplaced" && (
                                      <span className="text-sm font-medium bg-gray-200 text-gray-600 py-1 px-2 rounded">
                                        Unplaced
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-gray-500 text-sm">
                            No tips available for this race
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <BottomNavbar />
    </div>
  );
}
